import React, { useState, useRef } from "react";
import { Surface, Btn } from "./Primitives";
import { Camera, X, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Phase 6A — Read Hobbs/Tach meter from photo.
 * Posts a photo to /api/aircraft/{aid}/read-meter-photo, displays AI
 * reading + confidence + warnings, lets owner accept or discard.
 *
 * Props:
 *   aircraftId   string
 *   open         boolean
 *   onClose      ()  => void
 *   onApply      (reading: number, meterType: string) => void
 */
export default function HobbsPhotoModal({ aircraftId, open, onClose, onApply }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setBusy(false); setPreview(null); setResult(null); setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(""); setResult(null);
    setPreview(URL.createObjectURL(f));
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const r = await api.post(
        `/aircraft/${aircraftId}/read-meter-photo`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not read the meter");
    } finally {
      setBusy(false);
    }
  };

  const accept = () => {
    if (result?.reading != null) {
      onApply(result.reading, result.meter_type || "hobbs");
      reset(); onClose();
    }
  };

  const close = () => { reset(); onClose(); };

  const confColor = result
    ? (result.confidence >= 0.8 ? "text-emerald-400"
      : result.confidence >= 0.5 ? "text-amber" : "text-red-400")
    : "text-zinc-500";

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="hobbs-photo-modal">
      <Surface className="w-full max-w-lg p-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-medium flex items-center gap-2">
            <Camera size={20} className="text-amber" />
            Read meter from photo
          </h3>
          <button onClick={close}><X size={20} className="text-zinc-500" /></button>
        </div>

        <div className="mb-4 p-3 border border-amber/40 bg-amber/5 text-[11px] text-amber/90 font-mono">
          Snap a photo of your Hobbs / tach / engine-hours meter. Gemini Vision will read the digits and propose a value — you confirm before it overwrites the aircraft.
        </div>

        <div className="space-y-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
            onChange={handleFile}
            className="w-full text-xs text-zinc-400 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-amber file:text-black file:font-mono file:text-[10px] file:uppercase file:tracking-wider hover:file:bg-amber/90"
            data-testid="hobbs-photo-input"
          />

          {preview && (
            <div className="border border-zinc-800 bg-zinc-950 p-2">
              <img src={preview} alt="meter" className="w-full max-h-72 object-contain" />
            </div>
          )}

          {busy && (
            <div className="text-center text-xs text-zinc-400 font-mono py-3" data-testid="hobbs-photo-busy">
              Analysing photo (~5-10s)…
            </div>
          )}

          {result && !busy && (
            <div className="p-4 border border-zinc-800 bg-zinc-950" data-testid="hobbs-photo-result">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Reading</div>
                <div className={`text-[10px] font-mono ${confColor}`}>
                  {result.confidence != null ? `confidence ${Math.round(result.confidence * 100)}%` : ""}
                </div>
              </div>
              <div className="text-3xl font-semibold tabular-nums text-amber" data-testid="hobbs-photo-reading">
                {result.reading != null ? result.reading.toFixed(1) : "—"}
              </div>
              <div className="text-[11px] text-zinc-500 font-mono mt-1">
                Meter type: <span className="text-zinc-300">{result.meter_type}</span>
              </div>
              {result.warnings?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-800 space-y-1">
                  {result.warnings.map((w, i) => (
                    <div key={i} className="text-[11px] text-amber flex items-start gap-1.5">
                      <WarningCircle size={12} className="mt-0.5 shrink-0" /> {w}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 border border-red-900/60 bg-red-950/20 text-xs text-red-300 font-mono" data-testid="hobbs-photo-error">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Btn variant="ghost" onClick={close}>Cancel</Btn>
          <Btn onClick={accept} disabled={!result?.reading || busy}
               data-testid="hobbs-photo-apply-btn">
            <CheckCircle size={14} className="inline mr-1.5" />
            Apply reading
          </Btn>
        </div>
      </Surface>
    </div>
  );
}
